import {z} from 'zod';
import {TODO_STATUS} from './constants';

// ロゴURL型（企業ロゴのURLを複数のサイズで保持）
export const LogoURLs = z.object({
  '48x36': z.string(),
  '140x105': z.string(),
  '200x150': z.string(),
  '400x300': z.string(),
  default: z.string(),
});

export type LogoURLsType = z.infer<typeof LogoURLs>;

// TODOステータス
export const TodoStatusSchema = z.enum([
  TODO_STATUS.TODO,
  TODO_STATUS.DOING,
  TODO_STATUS.DONE,
]);

export type TodoStatusType = z.infer<typeof TodoStatusSchema>;

// 企業情報
export const CompanyInfo = z.object({
  companyID: z.number().nullable().optional(),
  companyName: z.string(),
  logoUrls: LogoURLs.optional(),
});

// エントリーTODO
export const EntryTodo = z.object({
  id: z.number(),
  userID: z.number(),
  userEntryManagementID: z.number().nullable(),
  userEntryManagementStepID: z.number().nullable(),
  status: TodoStatusSchema,
  title: z.string(),
  link: z.string(),
  body: z.string(),
  dueDate: z.string().nullable(),
  isAiGenerated: z.boolean(),
  priority: z.number().nullable(),
  position: z.number(),
  completedAt: z.string().nullable(),
  company: CompanyInfo.nullable().optional(),
  stepName: z.string().nullable().optional(),
  created: z.string(),
  modified: z.string(),
});

// GET /3.0/service/users/me/entry-todos - 一覧取得
export const GetEntryTodosOutput = z.object({
  todo: z.array(EntryTodo),
  doing: z.array(EntryTodo),
  done: z.array(EntryTodo),
  totalCount: z.number(),
});

// POST /3.0/service/users/me/entry-todos - 作成
export const CreateEntryTodoInput = z.object({
  companyID: z.number().nullable().optional(),
  companyName: z.string().optional(),
  userEntryManagementID: z.number().nullable().optional(),
  userEntryManagementStepID: z.number().nullable().optional(),
  title: z.string(),
  link: z.string().optional(),
  body: z.string().optional(),
  dueDate: z.string().optional(), // YYYY-MM-DD形式
  priority: z.number().min(1).max(3).nullable().optional(),
  isAiGenerated: z.boolean().optional(),
});

// PUT /3.0/service/users/me/entry-todos/{id} - 更新
export const UpdateEntryTodoInput = z.object({
  companyID: z.number().nullable().optional(),
  companyName: z.string().optional(),
  userEntryManagementID: z.number().nullable().optional(),
  userEntryManagementStepID: z.number().nullable().optional(),
  status: TodoStatusSchema.optional(),
  title: z.string().optional(),
  link: z.string().optional(),
  body: z.string().optional(),
  dueDate: z.string().optional(), // YYYY-MM-DD形式
  priority: z.number().min(1).max(3).nullable().optional(),
  position: z.number().optional(), // [Deprecated] 直接指定は非推奨
  targetStatus: TodoStatusSchema.optional(),
  previousTodoID: z.number().optional(),
  nextTodoID: z.number().optional(),
  isEdited: z.boolean().optional(),
});

// Google同期結果（CRUD操作時）
// NOTE: entryCalendars/schema.tsと同一定義だが、循環依存を避けるためローカル定義
const GoogleSyncResult = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

// POST /3.0/service/users/me/entry-todos - 作成レスポンス
export const CreateEntryTodoOutput = EntryTodo.extend({
  googleSyncResult: GoogleSyncResult.optional(),
});

// PUT /3.0/service/users/me/entry-todos/{id} - 更新レスポンス
export const UpdateEntryTodoOutput = EntryTodo.extend({
  googleSyncResult: GoogleSyncResult.optional(),
});

// DELETE /3.0/service/users/me/entry-todos/{id} - 削除
// Google連携時は200（同期結果あり）、未連携時は204 No Content
export const DeleteEntryTodoOutput = z.object({
  googleSyncResult: GoogleSyncResult.optional(),
});

// AI TODO生成用スキーマ

// TODO候補（DB保存前・id/created/modifiedなし）
export const TodoCandidate = z.object({
  title: z.string(),
  body: z.string(),
  link: z.string().optional(),
  dueDate: z.string().nullable(),
  priority: z.number().min(1).max(3).nullable(),
});

// POST /3.0/service/users/me/entry-todos/ai-generate - AI TODO生成
export const AiGenerateEntryTodosInput = z.object({
  stepID: z.number(),
  link: z.string().optional(),
  minCount: z.number().optional(),
  maxCount: z.number().optional(),
});

export const AiGenerateEntryTodosOutput = z.object({
  companyID: z.number().nullable().optional(),
  companyName: z.string(),
  stepID: z.number(),
  todos: z.array(TodoCandidate),
});

// GET /3.0/users/me/entry-todos/reminders - TODOリマインダー件数取得
export const TodoRemindersResponse = z.object({
  dueWithinThreeDays: z.number(),
  dueToday: z.number(),
  overdue: z.number(),
});
